import { utils } from '@digital-commerce/shared-utils'
import { object } from 'prop-types'
import { useContext } from 'react'

import localeContext from '../../../contexts/Locale'
import useTranslate from '../../../hooks/useTranslate'

import { formatCurrency } from '../../../utils/currency'

import NameAndPrice from '../../atoms/ItemDetails/NameAndPrice'
import {
  FinalPrice,
  OtherChargesAndCreditsContainer,
  PriceBreakdownContainer
} from '../../molecules/CartItem/PriceBreakdown'
import HeadingWithSubItems from '../../molecules/CartItem/PriceBreakdown/HeadingWithSubItems'
import {
  buildAdditionalDevices,
  buildAdditionalChargesList,
  buildInstallItemMonthlyAmountData,
  buildMonthlyAddons,
  buildRecurringDiscountsList,
  getItemPriceSeatsSubtitleText,
  modifyItemPriceToUseSeat,
  buildAdditionalCreditsAndDiscounts
} from './utils'

const {
  cart: { getMonthlyItemPrice, getDisplayName, getMonthlyTotalPrice }
} = utils

const OfferDetails = ({ businessConnect, cart }) => {
  const { lang } = useContext(localeContext)
  const translate = useTranslate()

  const totalMonthlyPriceSubTotal = getMonthlyTotalPrice(businessConnect)?.subTotal

  const businessConnectWithSeatPrice = modifyItemPriceToUseSeat(businessConnect)
  const recurringDiscountsDataList = buildRecurringDiscountsList(businessConnect, lang)
  const installItemMonthlyAmountData = buildInstallItemMonthlyAmountData(
    businessConnect,
    translate,
    lang
  )
  const monthlyAddonsData = buildMonthlyAddons(businessConnect, lang)
  const additionalDevicesData = buildAdditionalDevices(businessConnect, lang)
  const additionalChargesListData = buildAdditionalChargesList(cart, lang)
  const additionalCreditsAndDiscountsListData = buildAdditionalCreditsAndDiscounts(
    businessConnect,
    cart,
    lang
  )

  // BConnect has a specific display name format
  const customItemPrice = (
    <NameAndPrice
      name={`${getDisplayName(businessConnectWithSeatPrice)} ${getItemPriceSeatsSubtitleText(
        businessConnectWithSeatPrice,
        translate,
        lang
      )}`}
      price={formatCurrency(getMonthlyItemPrice(businessConnectWithSeatPrice)?.amount, lang)}
      after="/mo."
      id={businessConnectWithSeatPrice.id}
    />
  )

  const recurringDiscounts = recurringDiscountsDataList.map((discount) => (
    <NameAndPrice
      id={discount.id}
      key={discount.id}
      name={discount.name}
      price={discount.price}
      prefixIcon={discount.prefixIcon}
    />
  ))

  const installItemMonthlyAmount = installItemMonthlyAmountData && (
    <NameAndPrice
      name={installItemMonthlyAmountData.name}
      price={installItemMonthlyAmountData.price}
      id={installItemMonthlyAmountData.id}
    />
  )

  const monthlyAddons = (
    <HeadingWithSubItems headingText={translate('addons')} subItems={monthlyAddonsData} />
  )

  const additionalDevices = (
    <HeadingWithSubItems
      headingText={translate('additionalHardware')}
      subItems={additionalDevicesData}
    />
  )

  const additionalCharges = (
    <HeadingWithSubItems
      headingText={translate('additionalChargesAndCredits.additionalCharges')}
      subItems={additionalChargesListData}
    />
  )

  const additionalCreditsAndDiscounts = (
    <HeadingWithSubItems
      headingText={translate('additionalChargesAndCredits.additionalCreditsAndDiscounts')}
      subItems={additionalCreditsAndDiscountsListData}
    />
  )

  return (
    <>
      <PriceBreakdownContainer>
        {customItemPrice}
        {recurringDiscounts}
        {installItemMonthlyAmount}
        {monthlyAddons}
        {additionalDevices}
        <FinalPrice price={totalMonthlyPriceSubTotal} isMonthly />
      </PriceBreakdownContainer>
      <OtherChargesAndCreditsContainer
        listData={[...additionalChargesListData, ...additionalCreditsAndDiscountsListData]}
      >
        {additionalCharges}
        {additionalCreditsAndDiscounts}
      </OtherChargesAndCreditsContainer>
    </>
  )
}

export default OfferDetails

OfferDetails.propTypes = {
  businessConnect: object.isRequired,
  cart: object.isRequired
}
